/**
 * useBoardEditing — the board's structural-op state machine (W2, WYSIWYG
 * plan §3.2/§3.3/§7). Lives beside BoardPane, which stays a thin binding:
 * this hook owns the pending-action state (rename / add / edge-arm /
 * process-chooser), builds the extra context-menu items, intercepts node
 * selection while an edge is armed, and routes every commit through the
 * mutation service. NO board-internals (SpaceRenderer et al.) are touched —
 * all UI lands in BoardPane-owned chrome (menu + anchored popovers).
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { shortestUniqueSuffix } from "@archlang/engine";
import type { MenuItem } from "./nav/ContextMenu.js";
import { useModelMutations, type MutationResult } from "./mutations.js";
import { useModuleEditing } from "./useModuleEditing.js";
import { useWorkspaceModel } from "./WorkspaceModelContext.js";
import { useWorkspace } from "./WorkspaceContext.js";
import { useActiveFile } from "./WorkspaceStore.js";
import { useProcessPlaybackContextOptional } from "./ProcessPlaybackContext.js";
import { useActiveView } from "./ActiveViewContext.js";
import { useLayoutContextOptional } from "./LayoutContext.js";
import { useEffectiveViewName } from "../views/useEffectiveViewName.js";
import type { Indexes } from "../components/Sidebar.js";

/** Structural keydown shape — native KeyboardEvent and React's both fit. */
export interface BoardKeyEvent {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly target: EventTarget | null;
  preventDefault(): void;
  stopPropagation(): void;
}

export type BoardEditAction =
  | { kind: "rename"; targetId: string; currentName: string }
  | { kind: "addModule"; parentId: string | null }
  | { kind: "addInterface"; targetId: string }
  | { kind: "edgeChooser"; fromId: string; toId: string }
  | { kind: "moveModule"; targetId: string; options: readonly string[] };

export interface BoardEditingApi {
  /** Pending popover-backed action (BoardPane renders BoardEditPopover). */
  readonly action: BoardEditAction | null;
  /** Armed edge source ("click a target… Esc cancels" chip). */
  readonly edgeFrom: string | null;
  /** Transient status line (stale/refused feedback, delete notice). */
  readonly note: string | null;
  /** Chooser candidates: [processId, label] pairs, focal first. */
  readonly processChoices: readonly { id: string; label: string }[];

  /** Extra context-menu items for a board node. */
  menuItemsFor(id: string, closeMenu: () => void): MenuItem[];
  /** Chrome "+ Module" (root add, §3.4 → active file). */
  addRootModule(): void;
  /** Intercepts a board node click while an edge is armed. Returns true when
   *  the click was consumed (BoardPane skips its normal select handling). */
  interceptSelect(id: string | null): boolean;
  /** Keydown (document-level in BoardPane): F2 rename, Delete delete,
   *  ⌘Z undo, Esc cancels an armed edge. Skips typing targets itself. */
  onKeyDown(e: BoardKeyEvent, selectedId: string | null): void;

  // Popover commits (BoardEditPopover calls back):
  commitRename(newName: string): void;
  commitAddModule(kind: string, name: string): void;
  commitAddInterface(kind: string, name: string): void;
  commitEdge(processId: string | "new", newName?: string): void;
  commitMoveModule(destination: string): void;
  cancel(): void;
}

/** The Move-to picker's root sentinel (a module can't be named this — parens
 *  aren't identifier characters). */
export const MOVE_TO_ROOT = "(root)";

export function useBoardEditing(
  enabled: boolean,
  indexes: Indexes | null,
): BoardEditingApi | null {
  const store = useWorkspace();
  const activeFile = useActiveFile(store);
  const shared = useWorkspaceModel();
  const mutations = useModelMutations();
  const playback = useProcessPlaybackContextOptional();
  const [action, setAction] = useState<BoardEditAction | null>(null);
  const [edgeFrom, setEdgeFrom] = useState<string | null>(null);
  const [note, setNoteState] = useState<string | null>(null);
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // §3.3 step 3: the last chooser pick is sticky per session.
  const lastProcessRef = useRef<string | null>(null);

  // S16/S18 channel split: while a MODEL view is active, a board rename is
  // PRESENTATION — it routes into the view body (`style <target> { rename … }`
  // via upsertStyleClauseEdit), never the model declaration. A builder DRAFT
  // has no text to write into, so it keeps the model channel.
  const effectiveViewName = useEffectiveViewName();
  const { draftView } = useActiveView();
  const viewChannelName = draftView ? null : effectiveViewName;

  const renameTargetId = action?.kind === "rename" ? action.targetId : null;
  const renameBundle = useModuleEditing(enabled ? renameTargetId : null);

  // Under an ACTIVE VIEW the board renders PROJECTED nodes whose ids may be
  // synthetic: a `group by` fan-out copy (`g<i>::<originQual>`, engine copyId
  // minting) or a re-parented clone keyed by its PROJECTED qualified name.
  // Neither exists in the RAW-model `indexes`, so every gesture target must
  // first map back to its ORIGIN qualified name — otherwise the render id
  // leaks verbatim into the .arch text (`style g1::Shop.Ledger { … }` —
  // file corruption). The layout controller's plan bridge (`viewNodeIds`,
  // origin→board-ids) is the authoritative mapping; reverse it here.
  const layout = useLayoutContextOptional();
  const viewNodeIds = layout?.viewNodeIds ?? null;
  const originByNodeId = useMemo<ReadonlyMap<string, string> | null>(() => {
    if (!viewNodeIds) return null;
    const rev = new Map<string, string>();
    for (const [origin, ids] of viewNodeIds) for (const id of ids) rev.set(id, origin);
    return rev;
  }, [viewNodeIds]);

  /** Board node id → the ORIGIN qualified name every gesture addresses.
   *  Raw-model ids resolve through `indexes`; projected ids through the plan
   *  bridge; a copy id falls back to the qual it carries after `::` (plan-less
   *  mounts). Unknown ids pass through — the mutation layer's unaddressable-
   *  subject refusal is the backstop, so nothing unresolvable is ever written. */
  const originQualFor = useCallback(
    (id: string): string => {
      const raw = indexes?.moduleQualifiedNameById.get(id);
      if (raw !== undefined) return raw;
      const origin = originByNodeId?.get(id);
      if (origin !== undefined) return origin;
      const sep = id.indexOf("::");
      if (sep > 0) return id.slice(sep + 2);
      return id;
    },
    [indexes, originByNodeId],
  );

  const setNote = useCallback((text: string | null): void => {
    if (noteTimer.current) clearTimeout(noteTimer.current);
    setNoteState(text);
    if (text) noteTimer.current = setTimeout(() => setNoteState(null), 4000);
  }, []);

  const report = useCallback(
    (r: MutationResult): void => {
      if (r.status === "applied") setNote(null);
      else if (r.status === "stale") setNote("compile catching up — try again");
      else if (r.status === "refused") setNote(r.message);
      else setNote("not editable here");
    },
    [setNote],
  );

  // drawEdge / style-subject operand references: shortest unique suffix over
  // the QUALIFIED names of every RAW-model module (§4.11 hoisting mirror).
  // The target maps through `originQualFor` first, so a projected copy/clone
  // id addresses its ORIGIN declaration, never the synthetic render id.
  const refFor = useCallback(
    (id: string): string => {
      const all = indexes ? [...indexes.moduleQualifiedNameById.values()] : [];
      const target = originQualFor(id);
      return shortestUniqueSuffix(
        all.map((q) => q.split(".")),
        target.split("."),
      );
    },
    [indexes, originQualFor],
  );

  const processChoices = useMemo(() => {
    const procs = shared.model?.processes ?? [];
    const focal = playback?.activeProcessId ?? null;
    const items = procs.map((p) => ({
      id: (p.id ?? p.name) as string,
      label: p.name || "(anonymous)",
    }));
    // Focal first, then sticky last-pick, then the rest.
    items.sort((a, b) => {
      const rank = (x: { id: string }): number =>
        x.id === focal ? 0 : x.id === lastProcessRef.current ? 1 : 2;
      return rank(a) - rank(b);
    });
    return items;
  }, [shared.model, playback?.activeProcessId]);

  const finishEdge = useCallback(
    (fromId: string, toId: string): void => {
      // §3.3 step 1: a focal process takes the edge without a chooser.
      const focal = playback?.activeProcessId ?? null;
      if (focal) {
        report(mutations.mutateProcess(focal, { op: "appendStep", from: refFor(fromId), to: refFor(toId) }));
        return;
      }
      setAction({ kind: "edgeChooser", fromId, toId });
    },
    [playback?.activeProcessId, mutations, refFor, report],
  );

  return useMemo(() => {
    if (!enabled) return null;
    return {
      action,
      edgeFrom,
      note,
      processChoices,

      menuItemsFor(id, closeMenu) {
        const q = originQualFor(id);
        const name = q.split(".").pop() ?? id;
        return [
          {
            label: "Rename…",
            onClick: () => {
              closeMenu();
              setAction({ kind: "rename", targetId: id, currentName: name });
            },
          },
          {
            label: "Add module inside…",
            onClick: () => {
              closeMenu();
              setAction({ kind: "addModule", parentId: id });
            },
          },
          {
            label: "Add interface…",
            onClick: () => {
              closeMenu();
              setAction({ kind: "addInterface", targetId: id });
            },
          },
          {
            label: "Draw edge from here",
            onClick: () => {
              closeMenu();
              setEdgeFrom(id);
              setNote("click a target node — Esc cancels");
            },
          },
          {
            label: "Move to…",
            onClick: () => {
              closeMenu();
              // Candidate parents: every module EXCEPT the target's own
              // subtree (cycle — the engine refuses it anyway) + the root.
              const targetQ = originQualFor(id);
              const options = [
                MOVE_TO_ROOT,
                ...[...(indexes?.moduleQualifiedNameById.values() ?? [])].filter(
                  (q) => q !== targetQ && !q.startsWith(targetQ + "."),
                ),
              ];
              setAction({ kind: "moveModule", targetId: id, options });
            },
          },
          {
            // Destructive action — red, below a separator (DESIGN-SYSTEM §5 law 2).
            label: "Delete",
            danger: true,
            separator: true,
            onClick: () => {
              closeMenu();
              const r = mutations.mutateModule(id, { op: "deleteDecl" });
              report(r);
              if (r.status === "applied") setNote("deleted — remaining references render as stubs");
            },
          },
        ];
      },

      addRootModule() {
        setAction({ kind: "addModule", parentId: null });
      },

      interceptSelect(id) {
        if (!edgeFrom || !id) return false;
        if (id === edgeFrom) return true; // ignore self-click, stay armed
        const from = edgeFrom;
        setEdgeFrom(null);
        setNote(null);
        finishEdge(from, id);
        return true;
      },

      onKeyDown(e, selectedId) {
        const t = e.target as HTMLElement;
        if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable) return;
        if (e.key === "Escape" && edgeFrom) {
          setEdgeFrom(null);
          setNote(null);
          e.stopPropagation();
          return;
        }
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
          if (mutations.undo()) {
            e.preventDefault();
            e.stopPropagation();
          }
          return;
        }
        if (!selectedId) return;
        if (e.key === "F2") {
          const q = originQualFor(selectedId);
          setAction({ kind: "rename", targetId: selectedId, currentName: q.split(".").pop() ?? selectedId });
          e.preventDefault();
        } else if (e.key === "Delete" || e.key === "Backspace") {
          const r = mutations.mutateModule(selectedId, { op: "deleteDecl" });
          report(r);
          if (r.status === "applied") setNote("deleted — remaining references render as stubs");
          e.preventDefault();
        }
      },

      commitRename(newName) {
        const a = action;
        setAction(null);
        if (!newName) return;
        // View channel (S16/S18): display rename into the ACTIVE view's body.
        if (viewChannelName && a?.kind === "rename") {
          report(
            mutations.mutateView(viewChannelName, {
              op: "setStyleMember",
              selectorText: refFor(a.targetId),
              member: `rename ${JSON.stringify(newName)}`,
            }),
          );
          return;
        }
        // Model channel: the real rename (LSP reference rewrite / setName).
        if (!renameBundle) return;
        void renameBundle.rename(newName).then(report);
      },

      commitAddModule(kind, name) {
        const a = action;
        setAction(null);
        if (a?.kind !== "addModule") return;
        if (a.parentId) {
          report(
            mutations.mutateModule(a.parentId, {
              op: "addModule",
              kind: kind || "module",
              name: name || undefined,
            }),
          );
        } else {
          // Root add: the active .arch file hosts it, else the first .arch in
          // the workspace (§3.4 — the active tab may be a .md document, which
          // must never receive a declaration). Always correctable by moving
          // text; never a modal file picker.
          const archFiles = [...(shared.compiledSources?.keys() ?? [])].filter((k) =>
            k.endsWith(".arch"),
          );
          const active =
            activeFile && activeFile.endsWith(".arch")
              ? activeFile.startsWith("/")
                ? activeFile
                : "/" + activeFile
              : null;
          const file = active ?? archFiles[0];
          if (!file) return;
          report(
            mutations.addTopLevelModule({
              absFilePath: file,
              kind: kind || "module",
              name: name || undefined,
              braced: true,
            }),
          );
        }
      },

      commitAddInterface(kind, name) {
        const a = action;
        setAction(null);
        if (a?.kind !== "addInterface" || !kind || !name) return;
        report(mutations.mutateModule(a.targetId, { op: "addInterface", kind, name }));
      },

      commitEdge(processId, newName) {
        const a = action;
        setAction(null);
        if (a?.kind !== "edgeChooser") return;
        const from = refFor(a.fromId);
        const to = refFor(a.toId);
        if (processId === "new") {
          // Common ancestor = shared qualified-name prefix (§3.3 locality).
          const fq = originQualFor(a.fromId).split(".");
          const tq = originQualFor(a.toId).split(".");
          let i = 0;
          while (i < fq.length - 1 && i < tq.length - 1 && fq[i] === tq[i]) i++;
          const anchor = i > 0 ? fq.slice(0, i).join(".") : null;
          report(
            mutations.addProcessWithStep({
              name: newName?.trim() ? newName.trim() : null, // zero-prompt anonymous by default
              anchorModuleId: anchor,
              fileOfModuleId: a.fromId,
              from,
              to,
            }),
          );
        } else {
          lastProcessRef.current = processId;
          report(mutations.mutateProcess(processId, { op: "appendStep", from, to }));
        }
      },

      commitMoveModule(destination) {
        const a = action;
        setAction(null);
        if (a?.kind !== "moveModule" || !destination) return;
        report(
          mutations.moveModule(a.targetId, destination === MOVE_TO_ROOT ? null : destination),
        );
      },

      cancel() {
        setAction(null);
        setEdgeFrom(null);
        setNote(null);
      },
    };
  }, [
    enabled,
    action,
    edgeFrom,
    note,
    processChoices,
    indexes,
    mutations,
    report,
    setNote,
    finishEdge,
    renameBundle,
    activeFile,
    shared.compiledSources,
    refFor,
    originQualFor,
    viewChannelName,
  ]);
}
